import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Heading,
} from "@catchoftheday/cg-components";
import { keyframes, styled, themeGet } from "@catchoftheday/theme";

import { Shop } from "@header/types";

import { CategoryMenu } from "../CategoryMenu";

const TRANSITION_IN_DURATION = 300;
const TRANSITION_OUT_DURATION = 200; // shorter duration prevents timing conflicts with transition-in

const slideIn = keyframes`
    0% { opacity: 0; left: -300px; }
    100% { opacity: 1; left: 0px; }
`;
const slideOut = keyframes`
    0% { opacity: 1; left: 0px; }
    100% { opacity: 0; left: -300px; }
`;

const fadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;
const fadeOut = keyframes`
    0% { opacity: 1; }
    100% { opacity: 0; }
`;

type MenuContainerProps = FlexProps & {
  open: boolean;
};

const MenuContainer = styled(Flex)<MenuContainerProps>`
  position: fixed;
  flex-direction: column;
  top: 0px;
  left: 0px;
  width: 300px;
  height: 100vh;
  padding-top: 16px;
  background-color: ${(props) =>
    themeGet("colors.ui.backgroundColorSecondary")(props)};
  box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.25);
  z-index: 101;
  animation: ${(props) => (props.open ? slideIn : slideOut)}
    ${TRANSITION_IN_DURATION}ms ease;
`;

type OverlayProps = BoxProps & {
  open: boolean;
};

const Overlay = styled(Box)<OverlayProps>`
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 100;
  animation: ${(props) => (props.open ? fadeIn : fadeOut)}
    ${TRANSITION_IN_DURATION}ms ease;
`;

type SlideInMenuProps = {
  shops: Shop[];
  hideMenu: () => void;
};

export const SlideInMenu = ({ shops, hideMenu }: SlideInMenuProps) => {
  const [triggerAnimation, setTriggerAnimation] = useState<"open" | "close">(
    "open"
  );
  const currentScrollPosition = useRef<number>();

  const handleMenuClose = useCallback(() => {
    setTriggerAnimation("close");
  }, [setTriggerAnimation]);

  useEffect(() => {
    const closingAnimationTrigger = setTimeout(() => {
      if (triggerAnimation === "close") {
        hideMenu();
      }
    }, TRANSITION_OUT_DURATION);

    return () => clearTimeout(closingAnimationTrigger);
  }, [triggerAnimation, hideMenu]);

  useEffect(() => {
    // Should not be scrollable while the popup is active
    if (typeof window !== "undefined") {
      currentScrollPosition.current = document.documentElement.scrollTop;
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      return () => {
        window.scrollTo(0, currentScrollPosition.current || 0);
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("position");
        document.body.style.removeProperty("width");
      };
    }
  }, []);

  return (
    <>
      <Overlay
        open={triggerAnimation === "open"}
        onClick={handleMenuClose}
        aria-label="Menu overlay"
      />
      <MenuContainer
        open={triggerAnimation === "open"}
        aria-label="Side navigation category menu"
      >
        <Flex mb="14px" p="0px 16px 0px 12px" alignItems="center">
          <Box flex="1">
            <Heading.h3 fontSize="lg" mb="0px" mt="0px">
              Categories
            </Heading.h3>
          </Box>
          <Box
            fontWeight="normal"
            fontSize="sm"
            color="secondary"
            cursor="pointer"
            aria-label="Close menu"
            onClick={handleMenuClose}
          >
            Close
          </Box>
        </Flex>
        <CategoryMenu shops={shops} />
      </MenuContainer>
    </>
  );
};
